import {
  AppBar, Toolbar, Box, Button, IconButton, Drawer,
  List, ListItem, ListItemButton, ListItemText, Typography,
  Avatar, Menu, MenuItem, Divider
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import LockResetIcon from '@mui/icons-material/LockReset';
import LogoutIcon from '@mui/icons-material/Logout';
import { Outlet, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useContext, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import '@fontsource/montserrat/400.css';
import '@fontsource/montserrat/600.css';
import '@fontsource/montserrat/700.css';
import logo from '../assets/images/logo.png';
import NotificationsBell from './NotificationsBell';

const font = 'Montserrat, sans-serif';

const navLinkSx = {
  cursor: 'pointer',
  color: '#163244',
  fontWeight: 400,
  fontSize: '0.85rem',
  fontFamily: font,
  textDecoration: 'none',
  '&:hover': { textDecoration: 'underline' }
};

const Layout = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const handleLogout = () => {
    setAnchorEl(null);
    logout();
    navigate('/');
  };

  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()
    : '?';

  const drawer = (
    <Box sx={{ width: 250, fontFamily: font }} onClick={() => setMobileOpen(false)}>
      <List>
        <ListItemButton component={RouterLink} to="/services">
          <ListItemText primary="Servicios" primaryTypographyProps={{ fontFamily: font, fontWeight: 400 }} />
        </ListItemButton>

        {/* Admin */}
        {user?.role === 'admin' && (
          <ListItemButton component={RouterLink} to="/admin/users">
            <ListItemText primary="Usuarios" primaryTypographyProps={{ fontFamily: font, fontWeight: 400 }} />
          </ListItemButton>
        )}
        {user?.role === 'admin' && (
          <ListItemButton component={RouterLink} to="/admin/requests">
            <ListItemText primary="Solicitudes" primaryTypographyProps={{ fontFamily: font, fontWeight: 400 }} />
          </ListItemButton>
        )}
        {user?.role === 'admin' && (
          <ListItemButton component={RouterLink} to="/admin/prestadores">
            <ListItemText primary="Prestadores" primaryTypographyProps={{ fontFamily: font, fontWeight: 400 }} />
          </ListItemButton>
        )}
        {user?.role === 'admin' && (
          <ListItemButton component={RouterLink} to="/admin/formularios">
            <ListItemText primary="Formularios" primaryTypographyProps={{ fontFamily: font, fontWeight: 400 }} />
          </ListItemButton>
        )}

        {/* Cliente */}
        {user?.role === 'user' && (
          <ListItemButton component={RouterLink} to="/mis-solicitudes">
            <ListItemText primary="Mis solicitudes" primaryTypographyProps={{ fontFamily: font, fontWeight: 400 }} />
          </ListItemButton>
        )}

        {/* Prestador */}
        {user?.role === 'prestador' && (
          <ListItemButton component={RouterLink} to="/prestador/solicitudes">
            <ListItemText primary="Solicitudes" primaryTypographyProps={{ fontFamily: font, fontWeight: 400 }} />
          </ListItemButton>
        )}
        {user?.role === 'prestador' && (
          <ListItemButton component={RouterLink} to="/prestador/presupuestos">
            <ListItemText primary="Presupuestos" primaryTypographyProps={{ fontFamily: font, fontWeight: 400 }} />
          </ListItemButton>
        )}
        {user?.role === 'prestador' && (
          <ListItemButton component={RouterLink} to="/prestador/agenda">
            <ListItemText primary="Agenda" primaryTypographyProps={{ fontFamily: font, fontWeight: 400 }} />
          </ListItemButton>
        )}
        {user?.role === 'prestador' && (
          <ListItemButton component={RouterLink} to="/prestador/perfil">
            <ListItemText primary="Mi perfil prestador" primaryTypographyProps={{ fontFamily: font, fontWeight: 400 }} />
          </ListItemButton>
        )}

        <ListItemButton component={RouterLink} to="/perfil">
          <ListItemText primary="Mi perfil" primaryTypographyProps={{ fontFamily: font, fontWeight: 400 }} />
        </ListItemButton>
        <ListItemButton onClick={handleLogout}>
          <ListItemText primary="Cerrar sesión" primaryTypographyProps={{ fontFamily: font, fontWeight: 400 }} />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <>
      {/* ── Barra principal azul (igual que LandingPage) ── */}
      <AppBar position="fixed" sx={{ backgroundColor: '#163244', boxShadow: 1, zIndex: 1200 }}>

        {/* Fila 1: logo + nombre usuario + avatar */}
        <Toolbar sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>

          {/* Izquierda: hamburger mobile + logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              color="inherit"
              sx={{ display: { xs: 'flex', md: 'none' } }}
              onClick={() => setMobileOpen(true)}
            >
              <MenuIcon />
            </IconButton>
            <Box
              component={RouterLink} to="/"
              sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
            >
              <img src={logo} alt="Fixo Logo" height={40} />
            </Box>
          </Box>

          {/* Derecha: nombre + avatar con menú */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography
              sx={{
                color: '#E7A432', fontFamily: font, fontWeight: 400,
                fontSize: '0.85rem', display: { xs: 'none', sm: 'block' }
              }}
            >
              {user?.name}
            </Typography>
            <NotificationsBell />
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ p: 0.5 }}>
              <Avatar
                sx={{
                  width: 36, height: 36, backgroundColor: '#E7A432',
                  color: '#163244', fontSize: 14, fontWeight: 400, cursor: 'pointer'
                }}
              >
                {initials}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>

        {/* Fila 2: barra amarilla con links de navegación (igual que LandingPage) */}
        <Box sx={{ backgroundColor: '#E7A432', px: 4, py: 1 }}>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end', gap: 4 }}>
            <Typography component={RouterLink} to="/services" sx={navLinkSx}>
              Servicios
            </Typography>

            {/* Links exclusivos de admin */}
            {user?.role === 'admin' && (
              <Typography component={RouterLink} to="/admin/users" sx={navLinkSx}>
                Usuarios
              </Typography>
            )}
            {user?.role === 'admin' && (
              <Typography component={RouterLink} to="/admin/requests" sx={navLinkSx}>
                Solicitudes
              </Typography>
            )}
            {user?.role === 'admin' && (
              <Typography component={RouterLink} to="/admin/prestadores" sx={navLinkSx}>
                Prestadores
              </Typography>
            )}
            {user?.role === 'admin' && (
              <Typography component={RouterLink} to="/admin/formularios" sx={navLinkSx}>
                Formularios
              </Typography>
            )}

            {/* Links exclusivos de cliente (rol user) */}
            {user?.role === 'user' && (
              <Typography component={RouterLink} to="/mis-solicitudes" sx={navLinkSx}>
                Mis solicitudes
              </Typography>
            )}

            {/* Links exclusivos de prestador */}
            {user?.role === 'prestador' && (
              <Typography component={RouterLink} to="/prestador/solicitudes" sx={navLinkSx}>
                Solicitudes
              </Typography>
            )}
            {user?.role === 'prestador' && (
              <Typography component={RouterLink} to="/prestador/presupuestos" sx={navLinkSx}>
                Presupuestos
              </Typography>
            )}
            {user?.role === 'prestador' && (
              <Typography component={RouterLink} to="/prestador/agenda" sx={navLinkSx}>
                Agenda
              </Typography>
            )}
            {user?.role === 'prestador' && (
              <Typography component={RouterLink} to="/prestador/perfil" sx={navLinkSx}>
                Mi perfil prestador
              </Typography>
            )}

            <Typography component={RouterLink} to="/perfil" sx={navLinkSx}>
              Mi perfil
            </Typography>
          </Box>
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <Typography sx={{ color: '#163244', fontWeight: 400, fontSize: '0.8rem', fontFamily: font }}>
              Bienvenido, {user?.name}
            </Typography>
          </Box>
        </Box>
      </AppBar>

      {/* Menú desplegable del avatar */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{ elevation: 3, sx: { mt: 1, minWidth: 210, borderRadius: 2 } }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" fontWeight={400} sx={{ color: '#163244', fontFamily: font }}>
            {user?.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: font }}>
            {user?.email}
          </Typography>
        </Box>

        <MenuItem onClick={() => { navigate('/perfil'); setAnchorEl(null); }} sx={{ gap: 1.5, py: 1.2 }}>
          <PersonIcon fontSize="small" sx={{ color: '#163244' }} />
          <Typography variant="body2" sx={{ fontFamily: font }}>Mi perfil</Typography>
        </MenuItem>

        <MenuItem onClick={() => { navigate('/perfil'); setAnchorEl(null); }} sx={{ gap: 1.5, py: 1.2 }}>
          <LockResetIcon fontSize="small" sx={{ color: '#163244' }} />
          <Typography variant="body2" sx={{ fontFamily: font }}>Cambiar contraseña</Typography>
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleLogout} sx={{ gap: 1.5, py: 1.2, color: 'error.main' }}>
          <LogoutIcon fontSize="small" />
          <Typography variant="body2" sx={{ fontFamily: font }}>Cerrar sesión</Typography>
        </MenuItem>
      </Menu>

      {/* Drawer mobile */}
      <Drawer
        anchor="left" open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{ display: { xs: 'block', md: 'none' } }}
      >
        {drawer}
      </Drawer>

      {/* Contenido — margen superior para compensar AppBar fija (2 filas) */}
      <Box sx={{ pt: '80px' }}>
        <Outlet />
      </Box>
    </>
  );
};

export default Layout;